import type { RefObject } from 'react';
import React from 'react';

import { Spinner } from 'src/Spinner';

import { useForwardRef } from '../hooks/useForwardRef';
import { SlotWrapper } from '../Slot/SlotWrapper';
import { Text } from '../Text';
import { ControlSizeMapper } from '../Theme';
import { cnButton } from './Button.cn';
import { useButtonActions } from './Button.hooks/useButtonActions';
import { useButtonPalette } from './Button.hooks/useButtonPalette';
import { useIconButton } from './Button.hooks/useIconButton';
import type { ButtonProps } from './Button.types';
import { useHrButtonDefaultTooltipProps } from './constants';
import { ButtonIcon } from './Icon/ButtonIcon';
import { ButtonWrapper } from './Wrapper/ButtonWrapper';

import './Button.css';

export const Button = <TElement extends React.ElementType>(props: ButtonProps<TElement>) => {
    const {
        className,
        children,

        as: AsComponent = 'button',

        view = 'outline',
        size = 'm',
        stretch,

        isLoading,
        isThinking,
        isChecked,
        isDisabled,
        isReadOnly,
        isHoverOnText,

        icon,
        iconRight,
        hasSlots = true,
        slotLeft,
        slotRight,
        centeredSlotIcons,

        label,
        hasTooltip: givenHasTooltip,
        tooltipContent = label,
        hasOverflowTooltip: givenHasOverflowTooltip,
        tooltipProps,

        innerRef,
        disableFocusOnClick,

        type = AsComponent === 'button' ? 'button' : undefined,
        onClick,
        onMouseDown,
        onMouseUp,

        ...restProps
    } = props;

    const ref = useForwardRef<HTMLButtonElement>({
        forwardedRef: innerRef as RefObject<HTMLButtonElement>,
    });

    const { handleClick, handleMouseDown, handleMouseUp } = useButtonActions({
        ref,
        disableFocusOnClick,
        onClick,
        onMouseDown,
        onMouseUp,
        isLoading,
    });

    const { isIconButton, iconColor } = useIconButton({
        icon,
        view,
        isDisabled,
        children,
        isChecked,
    });

    const { containerPalette, contentPalette } = useButtonPalette({ view, isChecked, isDisabled });

    const iconLeft = icon && !isIconButton ? icon : undefined;
    const iconContent = icon && isIconButton ? icon : undefined;

    const { hrButtonDefaultTooltipProps } = useHrButtonDefaultTooltipProps();

    const mergedTooltipProps = React.useMemo(
        () => ({
            ...hrButtonDefaultTooltipProps,
            ...tooltipProps,
        }),
        [hrButtonDefaultTooltipProps, tooltipProps],
    );

    const hasTooltip = givenHasTooltip ?? (isIconButton && Boolean(label));

    const hasOverflowTooltip = (givenHasOverflowTooltip ?? !hasTooltip) && !isIconButton;

    const isCenteredSlotIcons = iconContent || centeredSlotIcons || size === 'xs';

    return (
        <ButtonWrapper
            tooltipContent={tooltipContent}
            hasTooltip={hasTooltip}
            tooltipProps={mergedTooltipProps}
            isIconButton={isIconButton}
        >
            <AsComponent
                className={cnButton(
                    {
                        view,
                        size,
                        isLoading,
                        isChecked,
                        isDisabled,
                        isReadOnly,
                        stretch,
                        isHoverOnText,
                        isInteractive: !isDisabled && !isReadOnly,
                        withoutSlots: !hasSlots,
                    },
                    [ControlSizeMapper[size], containerPalette, className],
                )}
                aria-disabled={isDisabled || isLoading || isThinking}
                aria-busy={isLoading || isThinking}
                aria-label={label}
                aria-pressed={isChecked}
                // Firefox сохраняет состояние disabled после перезагрузки
                // https://bugzilla.mozilla.org/show_bug.cgi?id=654072
                autoComplete="off"
                type={type}
                {...restProps}
                ref={ref}
                onClick={handleClick}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                disabled={isDisabled}
            >
                <span className={cnButton('Content', [contentPalette])}>
                    {hasSlots && (
                        <SlotWrapper
                            place="left"
                            type={isIconButton ? 'icon' : 'action'}
                            isDisabled={isDisabled}
                            className={cnButton('Slot')}
                            leftAddons={
                                iconLeft && (
                                    <ButtonIcon
                                        align={isCenteredSlotIcons ? undefined : 'right'}
                                        icon={icon}
                                        size={size}
                                    />
                                )
                            }
                        >
                            {slotLeft}
                        </SlotWrapper>
                    )}

                    <Text
                        maxVisibleLines={1}
                        // Если уже есть тултип, то еще один не нужен
                        overflowTooltipProps={hasOverflowTooltip ? mergedTooltipProps : null}
                        as="span"
                        className={cnButton('Text', { textContent: !iconContent })}
                        color={isThinking ? 'secondary' : undefined}
                        isThinking={isThinking && !isLoading}
                    >
                        {iconContent ? (
                            <ButtonIcon icon={iconContent} size={size} color={iconColor} />
                        ) : (
                            children
                        )}
                    </Text>

                    {hasSlots && (
                        <SlotWrapper
                            place="right"
                            type={isIconButton ? 'icon' : 'action'}
                            isDisabled={isDisabled}
                            className={cnButton('Slot')}
                            rightAddons={
                                iconRight && (
                                    <ButtonIcon
                                        align={isCenteredSlotIcons ? undefined : 'left'}
                                        icon={iconRight}
                                        size={size}
                                    />
                                )
                            }
                        >
                            {slotRight}
                        </SlotWrapper>
                    )}

                    <span className={cnButton('State')} />
                </span>

                {isLoading && (
                    <Spinner
                        size="xs"
                        className={cnButton('Spin', [contentPalette])}
                        position="center"
                    />
                )}
            </AsComponent>
        </ButtonWrapper>
    );
};
